import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { STUDY_EVENTS } from '../constants/telemetryEvents'
import { codingTimeIsUp, codingWindow, getStudyConfig } from './studyConfig'
import { useServerNow } from './useServerNow'
import { fetchProbes, forgetPendingAnswer, idKey, retryDelay } from './probeService'

// Where a study condition's student is (studyConfig.js), for Workspace.jsx:
//   'waiting'  before the instructor's Start (WorkspacePage shows the waiting room then)
//   'coding'   the timed exercise
//   'probes'   the coding time is up, or the student's tests all passed, and the server does
//              not have every probe answer yet
//   'done'     after that, or as soon as the coding ends when the condition has no probes
//              (the Thank You overlay, with the survey and the answers)
//   'extra'    the student chose "Keep working on the exercise" there: the workspace again,
//              never with the AI chat
// 'none' for every other session, which keeps its own flow exactly as before.
//
// Only students answer probes: an instructor opening a condition from Monitor goes straight
// to 'done', so their clicks never become study answers (the experiment's link turns staff
// away for the same reason).

export const isStudentRole = (user) => user?.role === 3 || user?.role === 4

// The probes start this long after a run that passed every test, so the student sees the
// green ticks first (as the CHI study's post-task survey waits).
export const PASSED_ALL_DELAY_MS = 3000

// Kept in this browser per session and student, so a reload keeps the student where they
// were: 'passed-all' once their tests all passed (the coding ended early), 'extra' once they
// chose to keep working after the Thank You overlay. Storage can be missing (private windows):
// the flag then lives as long as the page.
const flagKey = (name, sessionId, userId) => `study_${name}_${sessionId}_${userId}`
const readFlag = (name, sessionId, userId) => {
  try {
    return localStorage.getItem(flagKey(name, sessionId, userId)) === '1'
  } catch (error) {
    return false
  }
}
const storeFlag = (name, sessionId, userId) => {
  try {
    localStorage.setItem(flagKey(name, sessionId, userId), '1')
  } catch (error) {
    // Kept in memory only.
  }
}

// The page re-renders on every tick of the clock, so it ticks slowly until the end is near:
// every 15 s, then every second over the last 90 s. A slow tick can be 15 s stale, so the
// fast ones still begin at least 75 s before the end. Same idea as useTaskTimer, which only
// re-renders the workspace when its timer runs out.
const SLOW_TICK_MS = 15 * 1000
const FAST_TICK_MS = 1000
const FAST_BEFORE_END_MS = 90 * 1000
export const clockTickMs = (msLeft) => (msLeft > 0 && msLeft <= FAST_BEFORE_END_MS ? FAST_TICK_MS : SLOW_TICK_MS)

// A whole class reaches the end of the coding time in the same second. Each page waits a
// random moment before asking for the probes, which spreads those requests, and gives a page
// loaded just now time to learn the server's clock (useServerNow), so a computer clock that
// runs fast does not ask early.
const FIRST_LOAD_MIN_MS = 250
const FIRST_LOAD_SPREAD_MS = 2000

// probes: { status: 'loading' | 'ready', items, savedIds, failed, review }; savedIds are the
// items the server has answers for (the GET, then each POST's reply); review is the GET's
// (normalizeReview), present only when the server already had every answer. A condition whose row names a
// probe set has probes: nothing the server answers, a 404 included, turns that into 'done' -
// the page keeps loading until the questions come.
const LOADING = { status: 'loading', items: [], savedIds: new Set(), failed: false }

// The waits between GET attempts: about 2, 4, 8 and 15 s, then every 30 s (retryDelay's
// schedule from its second step), until the server answers.
const loadRetryDelay = (attempt) => retryDelay(attempt + 1)

// How many times a finished student's page asks for the answers (the review) before the Thank
// You overlay settles for its thanks alone: the review is extra, and a server that does not
// send it (a sign-in that does not match the student, say) will not start to.
const REVIEW_ATTEMPTS = 4

// finishedEarly: the student's tests all passed (students only); extra: they chose to keep
// working after the Thank You overlay.
export const studyPhaseOf = ({ study, session, nowMs, isStudent, probes = LOADING, finishedEarly = false, extra = false }) => {
  if (!study) return 'none'
  if (session?.status === 'pending') return 'waiting'
  // Before the clock: extra is only ever chosen after the coding ended, and a computer clock
  // behind the server's must not bring the coding (and its AI) back after a reload.
  if (isStudent && extra) return 'extra'
  if (!codingTimeIsUp(session, nowMs) && !(isStudent && finishedEarly)) return 'coding'
  if (!isStudent || !study.probeSet) return 'done'
  if (probes.status === 'ready' && probes.items.every((item) => probes.savedIds.has(idKey(item.id)))) return 'done'
  return 'probes'
}

// Whether Workspace shows its Thank You overlay ("You may now close this window"). Every
// other session shows it once closed, as before (the CHI study's sessions never). A study
// condition shows it in 'done' only: its row is closed by the instructor's page when Session
// Control's countdown ends - on the instructor's computer clock, so possibly before the
// coding time is up on the server's - and by a pause, neither of which ends the coding here.
export const showsThankYou = ({ phase, sessionClosed, isCHIStudy }) =>
  phase === 'none' ? Boolean(sessionClosed) && !isCHIStudy : phase === 'done'

// { phase, probes, markProbesSaved, review, surveyAnswered, finishedEarly, keepWorking, codingOver }
// for this session and student. passedAll: whether the student's latest run passed every test
// (Workspace); about 3 s after such a run the coding ends for them (finishedEarly, kept in this
// browser) and the probes start as when the time is up. PROBES_TRIGGERED is logged once per
// page when the probes start. surveyAnswered: the server has the Thank You overlay's survey.
// keepWorking: the overlay's "Keep working on the exercise" ('extra'); codingOver: whether the
// shared coding time is up, which alone ends a condition's AI. ProbeModal reports
// each answer the server confirmed through markProbesSaved; once every item is saved the phase
// turns to 'done'. A reload asks the server again when the time is up, so neither a finished
// student nor a half-finished one starts over. review: the answers with their explanations,
// for the Thank You overlay (ProbeReview) - from that GET on a reload after the last answer,
// else asked for once the phase turns 'done' - or null.
export const useStudyPhase = (session, currentUser, { passedAll = false, logTelemetryEvent } = {}) => {
  const isStudent = isStudentRole(currentUser)
  const sessionId = session?.id
  const userId = currentUser?.id
  // Once a study condition, always one for this page (as in TelemetryContext): a session
  // update that came without test_code must not drop the probes or unlock the workspace.
  const studyRef = useRef(null)
  const seenStudy = getStudyConfig(session)
  if (seenStudy) studyRef.current = { sessionId, study: seenStudy }
  const study = seenStudy || (studyRef.current?.sessionId === sessionId ? studyRef.current.study : null)
  // State from another session or student (the page can change both without remounting)
  // counts as nothing loaded.
  const owner = `${sessionId}:${userId}`

  const [fastClock, setFastClock] = useState(false)
  const nowMs = useServerNow(Boolean(study), fastClock ? FAST_TICK_MS : SLOW_TICK_MS)
  const coding = codingWindow(session)
  const wantsFastClock = clockTickMs(coding ? coding.endsAtMs - nowMs : null) === FAST_TICK_MS
  useEffect(() => {
    setFastClock(wantsFastClock)
  }, [wantsFastClock])

  // The flags this browser kept, then the ones set on this page (which win).
  const storedFlags = useMemo(
    () => ({ passedAll: readFlag('passed_all', sessionId, userId), extra: readFlag('extra', sessionId, userId) }),
    [sessionId, userId]
  )
  const [localFlags, setLocalFlags] = useState(null)
  const flags = localFlags?.owner === owner ? localFlags : storedFlags
  // The same student's other tab finishing early, or choosing to keep working, sets the flag
  // there: this tab follows (the probes start here too, and the AI chat goes).
  useEffect(() => {
    if (!sessionId || !userId) return undefined
    const keys = [flagKey('passed_all', sessionId, userId), flagKey('extra', sessionId, userId)]
    const onStorage = (event) => {
      if (!keys.includes(event.key)) return
      const passedAll = readFlag('passed_all', sessionId, userId)
      const extraChosen = readFlag('extra', sessionId, userId)
      setLocalFlags((current) => {
        const base = current?.owner === owner ? current : storedFlags
        return { ...base, owner, passedAll: base.passedAll || passedAll, extra: base.extra || extraChosen }
      })
    }
    window.addEventListener('storage', onStorage)
    return () => window.removeEventListener('storage', onStorage)
  }, [owner, sessionId, userId, storedFlags])
  const finishedEarly = Boolean(study) && isStudent && flags.passedAll
  const extra = Boolean(study) && isStudent && flags.extra
  const setFlag = useCallback(
    (name, field) => {
      storeFlag(name, sessionId, userId)
      setLocalFlags((current) => ({ ...(current?.owner === owner ? current : storedFlags), owner, [field]: true }))
    },
    [owner, sessionId, userId, storedFlags]
  )

  const [probeState, setProbeState] = useState(null)
  const probes = probeState?.owner === owner ? probeState : LOADING
  const loaded = probes.status !== 'loading'
  // Not in 'extra': the student answered every probe before they could choose it.
  const wantsProbes =
    Boolean(study?.probeSet) &&
    isStudent &&
    Boolean(sessionId) &&
    Boolean(userId) &&
    (codingTimeIsUp(session, nowMs) || finishedEarly) &&
    !extra

  useEffect(() => {
    if (!wantsProbes || loaded) return undefined
    let cancelled = false
    let timer = null
    const load = async (attempt) => {
      try {
        const result = await fetchProbes(sessionId, userId)
        if (cancelled) return
        // Copies kept in this browser of answers the server already has are done with.
        result.answeredItemIds.forEach((itemId) => forgetPendingAnswer(sessionId, userId, itemId))
        setProbeState({
          owner,
          status: 'ready',
          items: result.items,
          savedIds: new Set(result.answeredItemIds),
          failed: false,
          review: result.review,
          surveyAnswered: result.surveyAnswered === true,
        })
      } catch (error) {
        if (cancelled) return
        // Any failure, a 404 included (fetchProbes): still loading, as far as the phase
        // goes, so the workspace stays covered and the modal says it is still trying.
        setProbeState({ ...LOADING, owner, failed: true })
        timer = setTimeout(() => load(attempt + 1), loadRetryDelay(attempt))
      }
    }
    timer = setTimeout(() => load(0), FIRST_LOAD_MIN_MS + Math.random() * FIRST_LOAD_SPREAD_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [wantsProbes, loaded, owner, sessionId, userId])

  const markProbesSaved = useCallback((itemIds) => {
    setProbeState((state) => {
      if (state?.owner !== owner || state.status !== 'ready') return state
      return { ...state, savedIds: new Set([...state.savedIds, ...itemIds.map(idKey)]) }
    })
  }, [owner])

  const phase = studyPhaseOf({ study, session, nowMs, isStudent, probes, finishedEarly, extra })

  // A run that passed every test ends a student's coding about 3 s later. Another run in
  // between that does not pass them all, or the time running out first, cancels it.
  const canFinishEarly = phase === 'coding' && isStudent && Boolean(sessionId) && Boolean(userId)
  useEffect(() => {
    if (!canFinishEarly || !passedAll) return undefined
    const timer = setTimeout(() => setFlag('passed_all', 'passedAll'), PASSED_ALL_DELAY_MS)
    return () => clearTimeout(timer)
  }, [canFinishEarly, passedAll, setFlag])

  // PROBES_TRIGGERED, once per page and session: resumed when this page opened after the start
  // (a reload), never having seen the coding.
  const sawCodingFor = useRef(null)
  const triggerLoggedFor = useRef(null)
  useEffect(() => {
    if (phase === 'coding') sawCodingFor.current = owner
    if (phase !== 'probes' || triggerLoggedFor.current === owner) return
    triggerLoggedFor.current = owner
    logTelemetryEvent?.(STUDY_EVENTS.PROBES_TRIGGERED, {
      reason: finishedEarly ? 'passed-all' : 'time-up',
      resumed: sawCodingFor.current !== owner,
    })
  }, [phase, owner, finishedEarly, logTelemetryEvent])

  // Whether the shared coding time is up (the server's clock): only that ends a condition's AI
  // - finishing early does not (the research lead's rule, 2026-10-28).
  const codingOver = Boolean(study) && codingTimeIsUp(session, nowMs)

  const keepWorking = useCallback(() => {
    setFlag('extra', 'extra')
    logTelemetryEvent?.(STUDY_EVENTS.CONTINUED_WORKING, { codingOver })
  }, [setFlag, logTelemetryEvent, codingOver])

  const [reviewState, setReviewState] = useState(null)
  const review = probes.review || (reviewState?.owner === owner ? reviewState.review : null)
  // The answers, once the server has every one: a student who just answered the last
  // question asks for them (spread like the first request, as a class finishes together).
  const wantsReview = phase === 'done' && probes.status === 'ready' && !review

  useEffect(() => {
    if (!wantsReview) return undefined
    let cancelled = false
    let timer = null
    const load = async (attempt) => {
      try {
        const result = await fetchProbes(sessionId, userId)
        if (cancelled) return
        if (result.review) {
          setReviewState({ owner, review: result.review, surveyAnswered: result.surveyAnswered === true })
          return
        }
      } catch (error) {
        if (cancelled) return
      }
      if (attempt + 1 < REVIEW_ATTEMPTS) timer = setTimeout(() => load(attempt + 1), loadRetryDelay(attempt))
    }
    timer = setTimeout(() => load(0), FIRST_LOAD_MIN_MS + Math.random() * FIRST_LOAD_SPREAD_MS)
    return () => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [wantsReview, owner, sessionId, userId])

  const surveyAnswered = Boolean(probes.surveyAnswered || (reviewState?.owner === owner && reviewState.surveyAnswered))

  return { phase, probes, markProbesSaved, review, surveyAnswered, finishedEarly, keepWorking, codingOver }
}
